import React, { type MouseEventHandler } from 'react';
import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import { Accordion, useClassNameMapper } from 'react-bootstrap';
import Icons from '@aurora/shared-client/public/static/graphics/processed/enums';
import localStyles from './GuideAccordionToggle.module.pcss';

/**
 * Props for the GuideAccordion component.
 */
interface Props {
  /**
   * The ID of the guide item.
   */
  id: string;

  /**
   * The guide item title.
   */
  title: string;

  /**
   * A boolean indicating whether the section is open or not.
   */
  isOpen?: boolean;

  /**
   * Callback when button clicked.
   */
  onClick?: MouseEventHandler;
}

/**
 * The guide accordion toggle common component.
 *
 * @author Amit Agrawal, Willi Hyde
 */
const GuideAccordionToggle: React.FC<React.PropsWithChildren<Props>> = ({
  isOpen = false,
  onClick,
  children,
  title,
  id
}) => {
  const cx = useClassNameMapper(localStyles);

  return (
    <>
      <Accordion.Toggle
        as={Button}
        variant={ButtonVariant.UNSTYLED}
        className={cx('lia-guide-button')}
        eventKey={id}
        onClick={onClick}
      >
        <span className={cx('lia-icon-wrap')}>
          <Icon
            color={IconColor.GRAY_900}
            icon={Icons.ChevronRightIcon}
            size={IconSize.PX_14}
            className={cx('lia-icon', { 'lia-is-active': isOpen })}
          />
        </span>
        <span className={cx('lia-g-clamp lia-g-clamp-2')}>{title}</span>
      </Accordion.Toggle>
      <Accordion.Collapse eventKey={id}>
        <ul className={cx('lia-ul')}>
          <li className={cx('lia-li')}>{children}</li>
        </ul>
      </Accordion.Collapse>
    </>
  );
};

export default GuideAccordionToggle;
